import React, { createContext, useCallback, useContext, useEffect, useMemo, useReducer, useState } from 'react';
import { Alert } from 'react-bootstrap';
import { Outlet, useNavigate, Navigate } from 'react-router-dom';
// import { Redirect } from "react-router-dom";
import { useLocation } from 'react-router-dom';
import securityReducer from './securityReducer';

/**
 * @typedef {"LIMITED" | "STANDARD" | "SUPERUSER"} Role
 * @typedef {"OPS" | "CMT" | "DCT" | "FIN" | "TRADER" | "LSP" | "LOGISTICS" | "HAULIER" | "FREIGHT" | "QA"} Department
 * @typedef {[Department, Role]} DepartmentRolePair
 * @typedef {Department | Array<DepartmentRolePair | Department>} DepartmentProp
 * @typedef {Object} SecurityContextType
 * @property {import("./securityReducer").SecurityState} securityState
 * @property {import("React").ReducerWithoutAction} dispatch
 * @property {(config: SecurityConfiguration) => boolean} validateRole
 *
 * @typedef {Object} SecurityConfiguration
 * @property {SecurityObject[]} or
 * @property {DepartmentProp} department
 * @property {Role} role
 * @property {boolean} isLoggedIn
 * @property {boolean} isAdmin
 * @property {boolean} isCustomer
 */

/**
 * @typedef {Object} SecurityObject
 * @property {Department} department
 * @property {Role} role
 * @property {boolean} isCustomer
 * @property {boolean} isAdmin
 * @property {boolean} isLoggedIn
 */

const ROLE_MAP = {
  LIMITED: 1,
  STANDARD: 2,
  SUPERUSER: 3,
};

/**
 * @param {import("./securityReducer").SecurityState} userdata
 * @returns {boolean}
 */
export function isLimitedTraderOrWlpRole(userdata) {
  if (!userdata?.loggedIn) return false;
  const role = userdata.role?.toUpperCase?.();
  const dept = userdata.department?.toUpperCase?.();
  return role === 'LIMITED' && (dept === 'TRADER' || dept === 'WLP');
}

export const SecurityContext = createContext({});

const useQuery = () => {
  const { search } = useLocation();

  return useMemo(() => new URLSearchParams(search), [search]);
};

export function SecurityProvider({ children }) {
  const [state, dispatch] = useReducer(securityReducer, { loggedIn: false });
  const [sessionLoaded, setSessionLoaded] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();

  useEffect(() => {
    dispatch({ type: 'loadSession' });
    setSessionLoaded(true); // force the app to wait until we've loaded the session before rendering
  }, []);

  const query = useQuery();
  const authError = query.get('authError');

  const validateRole = useCallback(
    /**
     * @param {SecurityConfiguration} props
     */
    (props) => {
      if (Object.values(props).every((prop) => prop === undefined)) {
        return false;
      }
      const { or, department, role, isLoggedIn, isAdmin, isCustomer, isDutyUser } = props;
      if (Array.isArray(or)) {
        for (const secObj of or) {
          if (matchSecurityObject(state, secObj)) {
            return true;
          }
        }
      } else if (
        matchSecurityObject(state, {
          department,
          role,
          isAdmin,
          isLoggedIn,
          isCustomer,
          isDutyUser,
        })
      ) {
        return true;
      }
      return false;
    },
    [state]
  );

  const context = {
    securityState: state,
    dispatch,
    validateRole,
  };

  return (
    <SecurityContext.Provider value={context}>
      {sessionLoaded && children}
      {authError && (
        <Alert variant="danger" className="position-fixed bottom-0 end-0 alert-dismissible me-5 z-3">
          <Alert.Heading>Error Loading Page</Alert.Heading>
          <p>{authError}</p>
          <button
            type="button"
            class="btn-close"
            onClick={() => navigate(location.pathname.split('?')[0], { replace: true })}
          ></button>
        </Alert>
      )}
    </SecurityContext.Provider>
  );
}

/**
 *
 * @param {SecurityConfiguration & {children: import("react").ReactChildren}} props
 */
export function ShowIf({ children, ...rest }) {
  const { validateRole } = useSecurity();
  if (validateRole(rest)) {
    return children;
  } else {
    return null;
  }
}

/**
 *
 * @param {import("./securityReducer").SecurityState} userdata
 * @param {SecurityObject} matchAgainst
 */
function matchSecurityObject(userdata, matchAgainst) {
  if (matchAgainst.isLoggedIn !== undefined && !!userdata.loggedIn !== !!matchAgainst.isLoggedIn) return false;
  if (matchAgainst.isAdmin !== undefined && !!userdata.admin !== !!matchAgainst.isAdmin) return false;

  if (
    (process.env.REACT_APP_ENV === 'dev' || process.env.NODE_ENV === 'test') &&
    userdata.department?.toUpperCase() === 'DEV'
  )
    return true;

  if (matchAgainst.isCustomer !== undefined) {
    const userIsCustomer = ['TRADER', 'LOGISTICS', 'HAULIER', 'FREIGHT', 'LSP'].includes(
      userdata.department?.toUpperCase()
    );
    if (userIsCustomer !== matchAgainst.isCustomer) return false;
  }

  /**
   * CMT is the main customs-level role for CABIE and is granted the same access as
   * OPS and QA (any role), so it already has add/edit/remove/delete/disable rights
   * everywhere those departments do. Other gates (IT, WLP, customer-only) are left
   * untouched, so CMT still cannot see IT admin screens or customer flows.
   */
  // CMT should inherit broad access to OPS/QA when the gate is department-only
  if (
    userdata.department?.toUpperCase() === 'CMT' &&
    matchAgainst.department !== undefined &&
    matchAgainst.role === undefined
  ) {
    const targetDept = matchAgainst.department.toUpperCase?.();
    if (targetDept === 'OPS' || targetDept === 'QA') {
      return true;
    }
  }

  if (matchAgainst.department !== undefined) {
    if (matchAgainst.department.toUpperCase?.() !== userdata.department?.toUpperCase()) {
      return false;
    }
  }

  if (matchAgainst.role !== undefined) {
    if (ROLE_MAP[userdata.role.toUpperCase()] < ROLE_MAP[matchAgainst.role.toUpperCase?.()]) {
      return false;
    }
  }

  if (matchAgainst.isDutyUser !== undefined) {
    return matchAgainst.isDutyUser === userdata.duty;
  }

  return true;
}

export function PrivateRoute({ children, ...rest }) {
  const location = useLocation();
  const navigate = useNavigate();

  const { securityState } = useSecurity();

  return securityState.loggedIn ? <Outlet /> : <Navigate to="/login" state={{ from: location.pathname }} />;
}

/**
 *
 * @returns {SecurityContextType} securityContext
 */
export const useSecurity = () => {
  return useContext(SecurityContext);
};
